"use client"

import { useState, useRef, useEffect, useMemo } from 'react'
import { BedDouble, Bath, Maximize, Car, Archive, X } from 'lucide-react'
import { getOrientationDisplay } from '@/lib/constants'
import { getStatusDisplayName } from '@/lib/types/listing'
import { PaymentConfig } from '@/types/paymentConfig'
import LeadCaptureModal from '@/components/LeadCaptureModal'
// Import Google Material Icons
import ElevatorIcon from '@mui/icons-material/Elevator'
import ExploreIcon from '@mui/icons-material/Explore'
import SyncIcon from '@mui/icons-material/Sync'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'



interface Unit {
  id: string
  unitNumber: string
  unitType: string
  floor?: number
  bedrooms?: number
  bathrooms?: number
  halfBathrooms?: number
  interiorSurface?: number
  exteriorSurface?: number
  totalSurface?: number
  orientation?: string
  status?: string
  listPrice?: number
  typology?: string
}

type ProjectDetailsProps = {
  title: string
  description: string
  status?: string
  deliveryStatus?: string
  bedrooms: number
  bathrooms: number
  usableArea: number
  totalArea: number
  balconyArea: number | null
  orientation: string
  basePrice: number
  totalUnits: number
  availableUnits: number
  parking: number
  storage: number
  features: string[]
  units?: Unit[]
  paymentConfig?: PaymentConfig
  onUnitSelect?: (unit: Unit | null) => void
  projectImage?: string
  location?: string
  projectId?: string
}

// Define CSS keyframes for the animation
const SyncIconAnimation = () => (
  <style jsx global>{`
    @keyframes spin-ccw {
      from { transform: rotate(0deg); }
      to { transform: rotate(-360deg); }
    }
    .rotate-icon {
      transition: transform 0.3s ease;
    }
    .rotate-icon:hover {
      transform: translateX(4px);
    }
    .rotate-icon:hover .sync-icon {
      animation: spin-ccw 1.5s infinite linear;
      display: inline-block;
    }
  `}</style>
);

// Helper function to format UF values with max 2 decimal places
const formatUF = (amount: number) => {
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export default function ProjectDetails({
  title,
  description,
  status,
  deliveryStatus,
  bedrooms,
  bathrooms,
  usableArea,
  totalArea,
  balconyArea,
  orientation,
  basePrice,
  totalUnits,
  availableUnits,
  parking,
  storage,
  features,
  units,
  paymentConfig,
  onUnitSelect,
  projectImage,
  location,
  projectId
}: ProjectDetailsProps) {
  // State for popup visibility
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  // State for selected unit
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  // State for typology filter
  const [selectedTypologyFilter, setSelectedTypologyFilter] = useState<string | null>(null);
  // State for mobile sticky header visibility
  const [showMobileHeader, setShowMobileHeader] = useState(false);
  // State for lead capture modal
  const [showLeadModal, setShowLeadModal] = useState(false);
  
  // Memoize unit selection to prevent infinite re-renders
  const { selectedUnit, availableDepartamentoUnits } = useMemo(() => {
    
    let unit = null;
    let availableUnits: Unit[] = [];
    
    if (units && units.length > 0) {
      // Filter for available departamento units
      availableUnits = units.filter(u => {
        const isAvailable = u.status === 'available';
        // Accept all unit types except explicitly excluded ones (like parking spaces, storage, etc.)
        const isDepartamento = u.unitType && !['parking', 'storage', 'bodega', 'estacionamiento'].includes(u.unitType.toLowerCase());
        return isAvailable && isDepartamento;
      }).map(u => ({
        id: u.id,
        unitNumber: u.unitNumber,
        floor: u.floor,
        bedrooms: u.bedrooms,
        bathrooms: u.bathrooms,
        totalSurface: u.totalSurface,
        orientation: u.orientation,
        listPrice: u.listPrice,
        status: u.status,
        unitType: u.unitType,
        typology: u.typology
      }));
      
      // First, check if user has manually selected a unit
      if (selectedUnitId) {
        unit = availableUnits.find(u => u.id === selectedUnitId);
        
        // If selected unit is not in available departamentos, check all departamentos
        if (!unit) {
          const allDepartamentoUnits = units.filter(u => 
            u.unitType && !['parking', 'storage', 'bodega', 'estacionamiento'].includes(u.unitType.toLowerCase())
          ).map(u => ({
            id: u.id,
            unitNumber: u.unitNumber,
            floor: u.floor,
            bedrooms: u.bedrooms,
            bathrooms: u.bathrooms,
            totalSurface: u.totalSurface,
            orientation: u.orientation,
            listPrice: u.listPrice,
            status: u.status,
            unitType: u.unitType,
            typology: u.typology
          }));
          
          unit = allDepartamentoUnits.find(u => u.id === selectedUnitId);
        }
      }
      
      // If no user selection or user selected unit not found, fall back to cheapest available
      if (!unit && availableUnits.length > 0) {
        // Sort by listPrice
        const sortedUnits = [...availableUnits].sort((a, b) => {
          const priceA = a.listPrice !== null && a.listPrice !== undefined ? Number(a.listPrice) : Number.MAX_VALUE;
          const priceB = b.listPrice !== null && b.listPrice !== undefined ? Number(b.listPrice) : Number.MAX_VALUE;
          return priceA - priceB;
        });
        
        unit = sortedUnits[0];
      } else if (!unit) {
        // If no available departamento units, use any departamento unit
        const allDepartamentoUnits = units.filter(u => 
          u.unitType && !['parking', 'storage', 'bodega', 'estacionamiento'].includes(u.unitType.toLowerCase())
        ).map(u => ({
          id: u.id,
          unitNumber: u.unitNumber,
          floor: u.floor,
          bedrooms: u.bedrooms,
          bathrooms: u.bathrooms,
          totalSurface: u.totalSurface,
          orientation: u.orientation,
          listPrice: u.listPrice,
          status: u.status,
          unitType: u.unitType,
          typology: u.typology
        }));
        
        if (allDepartamentoUnits.length > 0) {
          unit = allDepartamentoUnits[0];
        }
      }
    }
    
    return { selectedUnit: unit, availableDepartamentoUnits: availableUnits };
  }, [units, selectedUnitId]);
    
  // Check if project has discount configured
  const hasProjectDiscount = paymentConfig?.components?.some(component => 
    component.type === 'descuento' && 
    'percentage' in component && 
    component.percentage > 0
  ) || false;
  
  // Get discount percentage if it exists
  const discountComponent = paymentConfig?.components?.find(component => 
    component.type === 'descuento' && 'percentage' in component
  );
  const discountPercentage = discountComponent && 'percentage' in discountComponent ? discountComponent.percentage : 0;
  
  // Get unit details
  const unitNumber = selectedUnit?.unitNumber || '101';
  const unitFloor = selectedUnit?.floor || 1;
  const unitListPrice = selectedUnit?.listPrice || basePrice;
  
  // Calculate prices based on discount
  const unitPrice = hasProjectDiscount ? unitListPrice * (1 - discountPercentage / 100) : unitListPrice;
  
  // Use selected unit's specific details instead of project-level values
  const unitBedrooms = selectedUnit?.bedrooms || bedrooms;
  const unitBathrooms = selectedUnit?.bathrooms || bathrooms;
  const unitTotalSurface = selectedUnit?.totalSurface || totalArea;
  const unitOrientation = selectedUnit?.orientation || orientation;
  const unitTypology = selectedUnit?.typology || (unitBedrooms === 0 ? 'Estudio' : `${unitBedrooms}D${unitBathrooms}B`);
  
  // Function to handle unit selection
  const handleUnitSelect = (unitId: string) => {
    setSelectedUnitId(unitId);
    setShowUnitSelector(false);
    // Reset filter when closing
    setSelectedTypologyFilter(null);
  };
  
  // Function to toggle unit selector popup
  const toggleUnitSelector = (show: boolean) => {
    setShowUnitSelector(show);
    // Reset filter when opening
    if (show) {
      setSelectedTypologyFilter(null);
      
      // Scroll to selected unit - simple approach
      setTimeout(() => {
        const selectedElement = document.getElementById(`unit-row-${selectedUnitId}`) || 
                              document.getElementById(`unit-card-${selectedUnitId}`);
        
        if (selectedElement) {
          selectedElement.scrollIntoView({ 
            behavior: 'auto', 
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 300);
    }
  };
  
  // Control body scroll when popup is shown/hidden
  useEffect(() => {
    if (showUnitSelector) {
      // Disable scrolling on body when popup is shown
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when popup is hidden
      document.body.style.overflow = '';
    }
    
    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [showUnitSelector]);

  // Handle scroll to show/hide mobile sticky header
  useEffect(() => {
    const handleScroll = () => {
      // Find the investment title section which is the first component content
      const investmentTitle = document.querySelector('[data-investment-title]');
      if (investmentTitle) {
        const rect = investmentTitle.getBoundingClientRect();
        // Show header when investment title is scrolled out of view (goes above viewport)
        setShowMobileHeader(rect.top < -50); // Add 50px buffer for smooth transition
      }
    };

    // Only add scroll listener on mobile
    if (window.innerWidth < 1024) { // lg breakpoint
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Notify parent when selected unit changes
  useEffect(() => {
    if (onUnitSelect) {
      onUnitSelect(selectedUnit || null);
    }
  }, [selectedUnit, onUnitSelect]);
    
  return (
    <>
      {/* Mobile Sticky Header - Only visible on mobile when scrolled */}
      {showMobileHeader && (
        <div 
          className="lg:hidden fixed inset-x-0 top-0 bg-white shadow-sm border-b border-gray-100 z-50 transition-all duration-300 ease-in-out animate-fade-in-up"
          style={{ margin: 0, padding: 0 }}
        >
          <div className="flex items-center p-4 space-x-3">
            {/* Project Image */}
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
              {projectImage ? (
                <img 
                  src={projectImage} 
                  alt={title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-500 text-xs font-medium">IMG</span>
                </div>
              )}
            </div>
            
            {/* Project Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-[#004132] font-inter leading-tight">
                {title}
              </h1>
              {location && (
                <p className="text-sm text-gray-600 font-inter truncate mt-1">
                  {location}
                </p>
              )}
            </div>
            
            {/* Status Tag */}
            {(deliveryStatus || status) && (
              <div className="flex-shrink-0">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#e6fa41] text-[#004132]">
                  {getStatusDisplayName(deliveryStatus || status || '')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Main Content - Add top and bottom padding on mobile for sticky header and button */}
      <div className={`bg-white rounded-lg shadow-sm p-6 relative lg:mt-0 lg:mb-0 mb-24 ${showMobileHeader ? 'mt-20' : 'mt-0'} transition-all duration-300`}>
        <SyncIconAnimation />
      
      {/* Investment Title */}
      <div className="mb-6" data-investment-title>
        <h2 className="text-xl sm:text-2xl font-thin text-[#004132] mb-3 font-hector" style={{fontWeight: 200}}>Inversión</h2>
        <div className="mb-2">
          <span className="text-lg sm:text-xl font-medium text-[#004132] font-inter">
            {unitTypology === 'Estudio' ? 'Estudio' : 'Departamento'} {unitNumber}
            {unitTypology && unitTypology !== 'Estudio' && (
              <span className="ml-2 text-sm bg-[#edfff8] text-[#004132] px-2 py-0.5 rounded-full">
                {unitTypology}
              </span>
            )}
          </span>
        </div>
        <div className="flex flex-col mb-3">
          {hasProjectDiscount ? (
            <>
              <div className="flex items-center mb-1">
                <span className="text-lg sm:text-xl text-gray-600 mr-2 font-inter">Precio lista</span>
                <span className="text-lg sm:text-xl text-gray-500 line-through font-inter">{formatUF(unitListPrice)} UF</span>
              </div>
              <div className="flex items-center">
                <span className="text-lg sm:text-xl text-gray-600 mr-2 font-inter">Precio promocional</span>
                <span className="text-lg sm:text-xl font-bold text-[#004132] font-inter">{formatUF(unitPrice)} UF</span>
              </div>
            </>
          ) : (
            <div className="flex items-center">
              <span className="text-lg sm:text-xl text-gray-600 mr-2 font-inter">Precio</span>
              <span className="text-lg sm:text-xl font-bold text-[#004132] font-inter">{formatUF(unitPrice)} UF</span>
            </div>
          )}
        </div>
        
        <div className="mb-3">
          <button 
            onClick={() => toggleUnitSelector(true)}
            className="px-3 py-1.5 text-sm bg-[#2de187] text-[#004132] font-bold rounded-full transition-colors font-inter flex items-center gap-1 shadow-sm rotate-icon"
          >
            <span className="sync-icon">
              <SyncIcon className="w-4 h-4" />
            </span>
            <span>Seleccionar otra unidad</span>
          </button>
        </div>
      </div>
      
      {/* Main specs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 sm:gap-2 mb-8">
        <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
          <BedDouble className="w-4 h-4 sm:w-5 sm:h-5 text-[#004132] mb-1" />
          <span className="text-xs text-gray-500 text-center">Dormitorios</span>
          <span className="font-bold text-[#004132] text-sm sm:text-base">{unitBedrooms}</span>
        </div>
        
        <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
          <Bath className="w-4 h-4 sm:w-5 sm:h-5 text-[#004132] mb-1" />
          <span className="text-xs text-gray-500 text-center">Baños</span>
          <span className="font-bold text-[#004132] text-sm sm:text-base">{unitBathrooms}</span>
        </div>
        
        <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
          <Maximize className="w-4 h-4 sm:w-5 sm:h-5 text-[#004132] mb-1" />
          <span className="text-xs text-gray-500 text-center leading-tight">Superficie total</span>
          <span className="font-bold text-[#004132] text-sm sm:text-base">{unitTotalSurface} m²</span>
        </div>
        
        <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
          {/* Elevator icon for floor */}
          <ElevatorIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#004132] mb-1" />
          <span className="text-xs text-gray-500 text-center">Piso</span>
          <span className="font-bold text-[#004132] text-sm sm:text-base">{unitFloor}</span>
        </div>
        <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
          {/* Explore icon for orientation */}
          <ExploreIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#004132] mb-1" />
          <span className="text-xs text-gray-500 text-center">Orientación</span>
          <span className="font-bold text-[#004132] capitalize text-sm sm:text-base">{getOrientationDisplay(unitOrientation)}</span>
        </div>
      </div>
      
      {/* Additional specs */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {balconyArea && balconyArea > 0 && (
            <div className="flex justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-600">Terraza</span>
              <span className="font-medium text-[#004132]">{balconyArea} m²</span>
            </div>
          )}
          
          {parking > 0 && (
            <div className="flex justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-600">Estacionamientos</span>
              <span className="font-medium text-[#004132]">{parking}</span>
            </div>
          )}
          
          {storage > 0 && (
            <div className="flex justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-600">Bodegas</span>
              <span className="font-medium text-[#004132]">{storage}</span>
            </div>
          )}
          

        </div>
      </div>
      
      {/* Description */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#004132] mb-4">Descripción</h2>
        <p className="text-gray-700 whitespace-pre-line">{description}</p>
      </div>
      
      {/* Features */}
      {features && features.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-[#004132] mb-4">Características</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center">
                <div className="w-2 h-2 bg-[#e6fa41] rounded-full mr-2"></div>
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Unit Selector Popup */}
      {showUnitSelector && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center backdrop-blur-sm lg:p-4"
          onClick={() => toggleUnitSelector(false)}
          style={{ paddingTop: window.innerWidth < 1024 ? '0' : '10vh' }} /* Full screen on mobile, centered on desktop */
        >
          <div 
            className="bg-white shadow-2xl w-full overflow-hidden border border-gray-100 transition-all duration-300 lg:rounded-xl lg:max-w-7xl"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: window.innerWidth < 1024 ? '100vh' : '80vh', height: window.innerWidth < 1024 ? '100vh' : 'auto' }} /* Full height on mobile */
          >
            <div className="p-5 border-b border-gray-100 flex flex-col gap-4 bg-gradient-to-r from-[#f8fffe] to-[#edfff8]">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-[#004132] flex items-center">
                  <SyncIcon className="mr-2 text-[#2de187]" />
                  Seleccionar Departamento
                </h3>
                <button 
                  onClick={() => toggleUnitSelector(false)}
                  className="text-gray-500 hover:text-[#004132] transition-colors p-1.5 rounded-full hover:bg-gray-100"
                  aria-label="Cerrar"
                >
                  <CloseIcon />
                </button>
              </div>
              
              {/* Typology Filter Buttons */}
              {(() => {
                // Get unique typologies from available units
                const typologies = availableDepartamentoUnits
                  .map(unit => {
                    if (unit.typology) return unit.typology;
                    if (unit.bedrooms === 0) return 'Estudio';
                    return `${unit.bedrooms}D${unit.bathrooms}B`; // Keep internal format as abbreviated
                  })
                  .filter((value, index, self) => self.indexOf(value) === index);
                
                // Function to format typology for display
                const formatTypologyForDisplay = (typology: string) => {
                  if (typology === 'Estudio') return 'Estudio';
                  
                  // Check if it's in the abbreviated format (e.g., "1D1B")
                  const match = typology.match(/^(\d+)D(\d+)B$/);
                  if (match) {
                    const bedrooms = parseInt(match[1]);
                    const bathrooms = parseInt(match[2]);
                    return `${bedrooms} Dormitorio${bedrooms !== 1 ? 's' : ''} ${bathrooms} Baño${bathrooms !== 1 ? 's' : ''}`;
                  }
                  
                  return typology; // Return as is if not in expected format
                };
                
                // Only show filters if there's more than one typology
                if (typologies.length > 1) {
                  return (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedTypologyFilter(null)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${selectedTypologyFilter === null
                          ? 'bg-[#004132] text-white font-medium'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        Todos
                      </button>
                      {typologies.map(typology => (
                        <button
                          key={typology}
                          onClick={() => setSelectedTypologyFilter(typology)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${selectedTypologyFilter === typology
                            ? 'bg-[#004132] text-white font-medium'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          {formatTypologyForDisplay(typology)}
                        </button>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            
            <div className="p-5 overflow-y-auto relative" style={{ maxHeight: window.innerWidth < 1024 ? 'calc(100vh - 120px)' : 'calc(80vh - 120px)' }}>
              {availableDepartamentoUnits.length > 0 ? (
                (() => {
                  // Check if project has discount configured in payment config
                  const hasDiscounts = paymentConfig?.components?.some(component => {
                    if (component.type === 'descuento') {
                      const hasPercentage = 'percentage' in component;
                      const percentageValue = 'percentage' in component ? component.percentage : 0;
                      return hasPercentage && percentageValue > 0;
                    }
                    return false;
                  }) || false;
                  
                  return (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden sm:block rounded-lg overflow-hidden border border-gray-200 shadow-sm overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr className="bg-[#f0fdf9]">
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Tipología</th>
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">N° unidad</th>
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Sup. Total</th>
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Piso</th>
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Orientación</th>
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Sup. Terraza</th>
                              {hasDiscounts && (
                                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Precio Promocional</th>
                              )}
                              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#004132] uppercase tracking-wider">Precio{hasDiscounts ? ' Lista' : ''}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {availableDepartamentoUnits
                              .filter(unit => {
                                if (selectedTypologyFilter === null) return true;
                                const unitTypology = unit.typology || (unit.bedrooms === 0 ? 'Estudio' : `${unit.bedrooms}D${unit.bathrooms}B`);
                                return unitTypology === selectedTypologyFilter;
                              })
                              .sort((a, b) => {
                                const priceA = a.listPrice !== null && a.listPrice !== undefined ? Number(a.listPrice) : Number.MAX_VALUE;
                                const priceB = b.listPrice !== null && b.listPrice !== undefined ? Number(b.listPrice) : Number.MAX_VALUE;
                                return priceA - priceB;
                              })
                              .map(unit => {
                                // If project has discount, calculate discounted price
                                const discountComponent = paymentConfig?.components?.find(component => 
                                  component.type === 'descuento' && 'percentage' in component
                                );
                                const discountPercentage = discountComponent && 'percentage' in discountComponent ? discountComponent.percentage : 0;
                                
                                const originalPrice = unit.listPrice || basePrice;
                                const displayPrice = hasDiscounts ? originalPrice * (1 - discountPercentage / 100) : originalPrice;
                                
                                return (
                                  <tr 
                                    key={unit.id}
                                    id={`unit-row-${unit.id}`}
                                    className={`transition-colors cursor-pointer ${selectedUnitId === unit.id 
                                      ? 'bg-[#edfff8] border-l-4 border-[#2de187] shadow-sm' 
                                      : 'hover:bg-gray-50'}`}
                                    onClick={() => handleUnitSelect(unit.id)}
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="font-medium text-[#004132] flex items-center gap-2">
                                        {selectedUnitId === unit.id && (
                                          <CheckCircleIcon className="text-[#2de187] w-5 h-5" />
                                        )}
                                        {unit.typology ? (
                                          <span className="bg-[#edfff8] text-[#004132] px-2 py-1 rounded-md">{unit.typology}</span>
                                        ) : unit.bedrooms === 0 ? (
                                          "Estudio"
                                        ) : (
                                          `${unit.bedrooms} Dormitorio${unit.bedrooms !== 1 ? 's' : ''} ${unit.bathrooms} Baño${unit.bathrooms !== 1 ? 's' : ''}`
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-gray-900 font-medium">{unit.unitNumber}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-gray-900">{unit.totalSurface} m²</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-gray-900">{unit.floor}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-gray-900 capitalize">{getOrientationDisplay(unit.orientation || '')}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-gray-900">{unit.exteriorSurface ? `${unit.exteriorSurface} m²` : '-'}</div>
                                    </td>
                                    {hasDiscounts && (
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-bold bg-[#2de187] text-[#004132] px-2 py-1 rounded-md inline-block">
                                          {formatUF(displayPrice)} UF
                                        </div>
                                      </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {hasDiscounts ? (
                                        <div className="text-gray-500 line-through">
                                          {formatUF(originalPrice)} UF
                                        </div>
                                      ) : (
                                        <div className="font-medium text-gray-900">
                                          {formatUF(displayPrice)} UF
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="block sm:hidden space-y-3">
                        {availableDepartamentoUnits
                          .filter(unit => {
                            if (selectedTypologyFilter === null) return true;
                            const unitTypology = unit.typology || (unit.bedrooms === 0 ? 'Estudio' : `${unit.bedrooms}D${unit.bathrooms}B`);
                            return unitTypology === selectedTypologyFilter;
                          })
                          .sort((a, b) => {
                            const priceA = a.listPrice !== null && a.listPrice !== undefined ? Number(a.listPrice) : Number.MAX_VALUE;
                            const priceB = b.listPrice !== null && b.listPrice !== undefined ? Number(b.listPrice) : Number.MAX_VALUE;
                            return priceA - priceB;
                          })
                          .map(unit => {
                            // If project has discount, calculate discounted price
                            const discountComponent = paymentConfig?.components?.find(component => 
                              component.type === 'descuento' && 'percentage' in component
                            );
                            const discountPercentage = discountComponent && 'percentage' in discountComponent ? discountComponent.percentage : 0;
                            
                            const originalPrice = unit.listPrice || basePrice;
                            const displayPrice = hasDiscounts ? originalPrice * (1 - discountPercentage / 100) : originalPrice;
                            
                            return (
                              <div 
                                key={unit.id}
                                id={`unit-card-${unit.id}`}
                                className={`relative p-3 border rounded-lg cursor-pointer transition-all duration-200 ${selectedUnitId === unit.id 
                                  ? 'bg-[#edfff8] border-[#2de187] shadow-md' 
                                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                                onClick={() => handleUnitSelect(unit.id)}
                              >
                                {/* Unit Header */}
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm font-semibold text-[#004132]">
                                      Departamento {unit.unitNumber}
                                    </div>
                                  </div>
                                  
                                  {/* Price */}
                                  <div className="text-right">
                                    {hasDiscounts ? (
                                      <div className="space-y-1">
                                        <div className="font-bold text-[#004132] text-sm bg-[#2de187] px-2 py-1 rounded-md">
                                          {formatUF(displayPrice)} UF
                                        </div>
                                        <div className="text-xs text-gray-500 line-through">
                                          {formatUF(originalPrice)} UF
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="font-bold text-[#004132] text-sm">
                                        {formatUF(displayPrice)} UF
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Unit Details Grid */}
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Sup. Total:</span>
                                    <span className="font-medium text-gray-900">{unit.totalSurface} m²</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Piso:</span>
                                    <span className="font-medium text-gray-900">{unit.floor}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Orientación:</span>
                                    <span className="font-medium text-gray-900 capitalize">{getOrientationDisplay(unit.orientation || '')}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Sup. Terraza:</span>
                                    <span className="font-medium text-gray-900">{unit.exteriorSurface ? `${unit.exteriorSurface} m²` : '-'}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  );
                })()
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 font-medium">No hay departamentos disponibles</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      
      {/* Mobile Sticky Bottom Button - Only visible on mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 z-40">
        <button 
          onClick={() => setShowLeadModal(true)}
          className="w-full bg-[#2de187] text-[#004132] font-bold py-4 px-6 rounded-xl text-lg font-inter transition-colors hover:bg-[#26cc7b] active:bg-[#22b86d] shadow-lg"
        >
          Quiero agendar
        </button>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        projectTitle={title}
        unitNumber={selectedUnit?.unitNumber}
        projectId={projectId}
        unitId={selectedUnit?.id}
      />
    </>
  )
}
